import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { SignatureAssetService } from 'src/service/SignatureAssetService';
import SignatureCanvas from 'react-signature-canvas';
import { FileUpload } from 'primereact/fileupload';

interface SignatureSelectorDialogProps {
    visible: boolean;
    onHide: () => void;
    onSelect: (base64: string) => void;
    expectedType?: string; // 'AgtHandwritingVerified' hoặc 'AgtImageVerified'
}

export const SignatureSelectorDialog = ({ visible, onHide, onSelect, expectedType }: SignatureSelectorDialogProps) => {
    const [signatures, setSignatures] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasAccount, setHasAccount] = useState(true);

    const [drawVisible, setDrawVisible] = useState(false);
    const [uploadVisible, setUploadVisible] = useState(false);

    useEffect(() => {
        if (visible) {
            loadSignatures();
        }
    }, [visible]);

    const loadSignatures = async () => {
        try {
            setLoading(true);
            const data = await SignatureAssetService.getPartySignatureAssets();
            const list = Array.isArray(data) ? data : (data?.partySignatureAssets || data?.data || []);
            
            // Lọc theo loại chữ ký nếu cần. Nếu expectedType truyền vào thì có thể báo gợi ý, nhưng ở đây tạm lấy log hết.
            setSignatures(list);
            setHasAccount(true);
        } catch (error: any) {
            // Nếu lỗi 401 hoặc lỗi không lấy được (vd: guest user), ẩn tab bộ sưu tập
            setHasAccount(false);
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (sig: any) => {
        if (sig.contentLocation && sig.contentLocation.startsWith('data:image')) {
            return sig.contentLocation;
        }
        return `/api/rest/s1/iam-signature-assets/resource?signatureAssetId=${sig.signatureAssetId}`;
    };

    // Hàm biến URL thành Base64 để trả về (vì lúc hiển thị List thì dùng URL Minio, nhưng khi Sign thì cần raw Base64)
    const selectSignature = async (sig: any) => {
        if (sig.contentLocation && sig.contentLocation.startsWith('data:image')) {
            onSelect(sig.contentLocation);
            onHide();
            return;
        }
        // Fetch ảnh từ resource API và convert sang base64
        try {
            const response = await fetch(getImageUrl(sig));
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
                onSelect(reader.result as string);
                onHide();
            };
            reader.readAsDataURL(blob);
        } catch (e) {
            console.error('Lỗi khi lấy ảnh chữ ký', e);
        }
    };

    return (
        <Dialog 
            header="Chọn chữ ký" 
            visible={visible} 
            onHide={onHide} 
            style={{ width: '600px' }}
        >
            <TabView>
                {hasAccount && (
                    <TabPanel header="Chữ ký đã lưu">
                        {loading ? (
                            <div className="flex justify-content-center py-4"><i className="pi pi-spin pi-spinner text-2xl"></i></div>
                        ) : signatures.filter(s => expectedType ? (s.signatureTypeEnumId === expectedType || (expectedType === 'AgtImageVerified' && s.signatureTypeEnumId === 'AgtHandwritingVerified') || (expectedType === 'AgtHandwritingVerified' && s.signatureTypeEnumId === 'AgtImageVerified')) : true).length === 0 ? (
                            <div className="text-center py-4 text-500">Không có chữ ký phù hợp được lưu.</div>
                        ) : (
                            <div className="grid mt-2">
                                {signatures
                                    .filter(s => expectedType ? (s.signatureTypeEnumId === expectedType || (expectedType === 'AgtImageVerified' && s.signatureTypeEnumId === 'AgtHandwritingVerified') || (expectedType === 'AgtHandwritingVerified' && s.signatureTypeEnumId === 'AgtImageVerified')) : true)
                                    .map((sig, idx) => (
                                    <div key={idx} className="col-12 md:col-6">
                                        <div 
                                            className="card surface-card border-round border-1 surface-border cursor-pointer hover:border-primary p-3 text-center flex flex-column align-items-center"
                                            onClick={() => selectSignature(sig)}
                                        >
                                            <div style={{ height: '80px' }} className="flex align-items-center justify-content-center">
                                                <img src={getImageUrl(sig)} alt={sig.signatureName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                            </div>
                                            <span className="mt-2 text-sm font-medium text-color-secondary">{sig.signatureName}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabPanel>
                )}
                
                <TabPanel header="Ký mới ngay">
                    <div className="flex flex-column gap-3 py-3 align-items-center">
                        <p className="text-center text-color-secondary">Vẽ chữ ký tay trực tiếp hoặc tải lên tệp ảnh từ máy tính.</p>
                        <div className="flex gap-4">
                            <Button label="Vẽ chữ ký" icon="pi pi-pencil" className="p-button-outlined" onClick={() => setDrawVisible(true)} />
                            <Button label="Tải ảnh lên" icon="pi pi-upload" onClick={() => setUploadVisible(true)} />
                        </div>
                    </div>
                </TabPanel>
            </TabView>

            {/* Các Dialog con chặn việc call API tạo mới asset nếu là khách, nhưng tạm thời dùng chung form và bỏ qua create API nếu guest, hoặc sửa lại Logic. 
                Vì form gốc create asset gọi SignatureAssetService.createSignatureAsset.
                Để an toàn, mượn component nhưng hook lại onSave. Nhưng vì form gốc gọi thẳng API, nếu là Guest sẽ lỗi 401. 
                Nên tạm viết 1 UI nhỏ nội bộ hoặc pass prop để không gọi API.
            */}
            {/* Tạm viết Inline Signature Pad để bypass API for guest */}
            <InlineDrawUpload 
                visible={drawVisible || uploadVisible} 
                mode={drawVisible ? 'draw' : 'upload'}
                onHide={() => { setDrawVisible(false); setUploadVisible(false); }}
                onSelect={(base64) => { onSelect(base64); setDrawVisible(false); setUploadVisible(false); onHide(); }}
            />
        </Dialog>
    );
};

// Component nội bộ để bypass API calls cho cả Guest & User, chỉ lấy base64 gán vào ô

const InlineDrawUpload = ({ visible, mode, onHide, onSelect }: any) => {
    const padRef = React.useRef<SignatureCanvas>(null);
    const [imageBase64, setImageBase64] = useState<string>('');

    const handleConfirm = () => {
        if (mode === 'draw') {
            if (padRef.current && !padRef.current.isEmpty()) {
                onSelect(padRef.current.getTrimmedCanvas().toDataURL('image/png'));
            }
        } else {
            if (imageBase64) onSelect(imageBase64);
        }
    };

    const handleUpload = (e: any) => {
        let file = e.files[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => setImageBase64(reader.result as string);
        }
    };

    return (
        <Dialog header={mode === 'draw' ? "Vẽ chữ ký" : "Tải ảnh"} visible={visible} onHide={onHide} style={{ width: '400px' }}>
            <div className="py-3">
                {mode === 'draw' ? (
                    <div className="border-1 surface-border bg-white" style={{ height: '200px' }}>
                        <SignatureCanvas ref={padRef} canvasProps={{ className: 'w-full h-full' }} backgroundColor="rgba(255,255,255,0)" />
                    </div>
                ) : (
                    <FileUpload 
                        name="demo[]" mode="basic" accept="image/*" customUpload onSelect={handleUpload}
                        chooseLabel="Chọn file" className="w-full"
                    />
                )}
            </div>
            <div className="flex justify-content-end gap-2 mt-4">
                {mode === 'draw' && <Button label="Xóa nội dung" icon="pi pi-refresh" text onClick={() => padRef.current?.clear()} />}
                <Button label="Xác nhận" icon="pi pi-check" onClick={handleConfirm} disabled={mode === 'upload' && !imageBase64} />
            </div>
        </Dialog>
    );
};
